import {
  alpha,
  avatarClasses,
  badgeClasses,
  buttonBaseClasses,
  checkboxClasses,
  chipClasses,
  formHelperTextClasses,
  inputClasses,
  touchRippleClasses
} from "@mui/material";
import { createDarkPalette } from "./colors/createDarkPalette.js";
import { createLightPalette } from "./colors/createLightPalette.js";
import { createShadows } from "./colors/createShadows.js";
import { createTidalColorPalettes } from "./colors/createTidalColorPalettes.js";
import { createChipFilledOutlineVariantStyles } from "./components/chip.js";
function createTidalThemeOptions(colorMode) {
  const { tidalColorsDark, tidalColorsLight } = createTidalColorPalettes();
  const tidalColors = colorMode === "light" ? tidalColorsLight : tidalColorsDark;
  return {
    colorSchemeSelector: '[data-mui-color-scheme="%s"]',
    colorSchemes: {
      dark: {
        palette: createDarkPalette(tidalColorsDark)
      },
      light: {
        palette: createLightPalette(tidalColorsLight)
      }
    },
    components: {
      MuiAvatar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.vars.palette.tidalColors.slateBlueGray[600]
          })
        },
        variants: [
          {
            props: {
              variant: "outlined"
            },
            // the css variables makes easier to override the border color
            // in the MuiAvatarGroup styleOverrides because the className generated
            // by the `outlinedRed` variant is not inside the avatarClasses object
            style: ({ theme }) => ({
              "--avatar-border-color": theme.vars.palette.tidalColors.slateBlueGray[100],
              border: `2px solid var(--avatar-border-color)`
            })
          },
          {
            props: {
              variant: "outlinedRed"
            },
            style: ({ theme }) => ({
              "--avatar-border-color": theme.vars.palette.tidalColors.lobsterRed[500],
              border: `2px solid var(--avatar-border-color)`
            })
          }
        ]
      },
      MuiAvatarGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`& .${avatarClasses.root}`]: {
              borderColor: `var(--avatar-border-color, ${theme.vars.palette.tidalColors.slateBlueGray[100]})`
            }
          })
        }
      },
      MuiBackdrop: {
        styleOverrides: {
          invisible: {
            backgroundColor: "transparent"
          },
          root: ({ theme }) => ({
            // intentionally not using theme.vars, channel does not exist for slateBlueGray
            backgroundColor: alpha(theme.palette.tidalColors.slateBlueGray.A700, 0.55)
          })
        }
      },
      MuiBadge: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { variant: "dotSpaced" },
                style: {
                  [`& .${badgeClasses.badge}`]: {
                    color: "transparent",
                    transform: "scale(0.4) translate(calc(50% + 6px), calc(-50% - 6px))"
                  }
                }
              }
            ]
          }
        }
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            [`& .${buttonBaseClasses.root}`]: {
              backgroundColor: "transparent"
            }
          }
        }
      },
      MuiButton: {
        variants: [
          {
            // see: https://workwave.atlassian.net/browse/CUI-234
            props: { color: "error" },
            style: ({ theme }) => ({
              [`.${touchRippleClasses.rippleVisible}`]: {
                backgroundColor: theme.vars.palette.tidalColors.lobsterRed[500],
                opacity: 0.2
              }
            })
          }
        ]
      },
      MuiCardActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2),
            paddingTop: theme.spacing(1)
          })
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2)
          })
        }
      },
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingBottom: theme.spacing(1)
          })
        }
      },
      MuiChip: {
        styleOverrides: {
          iconMedium: {
            fontSize: "1.25rem"
          },
          iconSmall: {
            fontSize: "1.25rem"
          },
          root: ({ theme, ownerState }) => {
            return {
              color: theme.vars.palette.text.primary,
              [`& .${chipClasses.deleteIcon}`]: {
                color: theme.vars.palette.action.active,
                marginLeft: theme.spacing(-1)
              },
              [`& .${chipClasses.sizeSmall} > ${chipClasses.deleteIcon}`]: {
                marginLeft: theme.spacing(-0.5)
              },
              [`& .${chipClasses.avatar}`]: {
                color: theme.vars.palette.text.primary
              },
              ...ownerState?.color === "error" && ownerState?.variant === "filled" && {
                color: theme.vars.palette.primary.contrastText
              },
              ...(ownerState?.color === "error" || ownerState?.color === "success" || ownerState?.color === "warning") && ownerState?.variant === "filled" && {
                [`& .${chipClasses.icon}`]: {
                  color: theme.vars.palette.primary.contrastText
                }
              },
              ...ownerState?.color === "error" && ownerState?.variant === "filled" && {
                [`& .${chipClasses.deleteIcon}:hover`]: {
                  color: `rgba(${theme.vars.palette.action.activeChannel} / 0.87)`
                }
              },
              ...ownerState?.variant === "filled" && {
                [`& .${chipClasses.avatar}`]: {
                  backgroundColor: theme.vars.palette.primary.contrastText
                }
              },
              ...ownerState?.variant === "filled" && ownerState?.color === "primary" && {
                [`& .${chipClasses.avatar}`]: {
                  color: theme.vars.palette.text.primary
                },
                [`& .${chipClasses.avatarColorPrimary}`]: {
                  backgroundColor: theme.vars.palette.primary.contrastText
                }
              },
              ...(ownerState?.color === "error" || ownerState?.color === "success" || ownerState?.color === "warning") && ownerState?.variant === "outlined" && {
                [`& .${chipClasses.icon}`]: {
                  color: theme.vars.palette[ownerState.color].main
                }
              },
              ...ownerState?.color === "primary" && ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatar}`]: {
                  // should be avatar-fill but in our codebase this variable does not exist
                  backgroundColor: theme.vars.palette.grey[400],
                  color: theme.vars.palette.text.primary
                }
              },
              ...ownerState?.color === "secondary" && ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatar}`]: {
                  // should be avatar-fill but in our codebase this variable does not exist
                  backgroundColor: theme.vars.palette.grey[400]
                }
              },
              ...Boolean(ownerState?.disabled) && {
                backgroundColor: theme.vars.palette.action.disabledBackground,
                borderColor: theme.vars.palette.action.disabled,
                color: theme.vars.palette.text.disabled,
                // if !important is not set, the style is overridden
                opacity: "1 !important",
                [`& .${chipClasses.icon}`]: {
                  color: theme.vars.palette.action.disabled
                },
                [`& .${chipClasses.deleteIcon}`]: {
                  color: theme.vars.palette.action.disabled
                },
                [`& .${chipClasses.deleteIcon}:hover`]: {
                  color: theme.vars.palette.action.disabled
                },
                [`& .${chipClasses.avatar}`]: {
                  backgroundColor: theme.vars.palette.action.disabledBackground,
                  color: theme.vars.palette.action.disabled
                }
              },
              ...ownerState?.variant === "outlined" && Boolean(ownerState?.disabled) && {
                backgroundColor: "transparent"
              },
              // change avatar background color to match default color
              // for primary and secondary color, the others are fine as it is
              ...ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatarColorPrimary}`]: {
                  backgroundColor: theme.vars.palette.tidalColors.slateBlueGray[600]
                },
                [`& .${chipClasses.avatarColorSecondary}`]: {
                  backgroundColor: theme.vars.palette.tidalColors.slateBlueGray[600]
                }
              },
              ...ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatar}`]: {
                  color: theme.palette.primary.contrastText
                }
              },
              variants: createChipFilledOutlineVariantStyles()
            };
          },
          sizeMedium: ({ theme }) => ({
            fontSize: theme.typography.body2.fontSize
          })
        }
      },
      MuiDialog: {
        defaultProps: {
          PaperProps: {
            elevation: 7
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: ({ theme }) => {
            return {
              // target form control label that contains checkbox than select the form helper text next to it
              [`&:has(.${checkboxClasses.root}) ~ .${formHelperTextClasses.root}`]: {
                marginLeft: `calc(1.5rem + ${theme.spacing(1)})`
                // 24px + 8px
              }
            };
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: () => {
            return {
              variants: [
                {
                  props: ({ ownerState }) => {
                    const sizeMedium = ownerState?.className?.includes("MuiInputLabel-sizeMedium") ?? false;
                    const shrink = ownerState?.["data-shrink"] === true;
                    return sizeMedium && shrink;
                  },
                  style: {
                    // can't find another method to target both size medium and shrink
                    // if !important is not set, the style is overridden by the default style
                    transform: "translate(14px, -9px) scale(0.875) !important"
                  }
                }
              ]
            };
          }
        }
      },
      MuiMenu: {
        defaultProps: {
          PaperProps: {
            elevation: 6
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { size: "medium" },
                style: {
                  legend: {
                    fontSize: "0.875rem"
                  }
                }
              }
            ]
          }
        }
      },
      MuiRating: {
        styleOverrides: {
          label: ({ theme }) => ({
            color: theme.vars.palette.warning.main
          }),
          readOnly: ({ theme }) => ({
            color: theme.vars.palette.warning.main
          })
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`&.${inputClasses.disabled}`]: {
              backgroundColor: theme.vars.palette.grey["100"]
            }
          })
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`& > .${inputClasses.disabled}`]: {
              backgroundColor: theme.vars.palette.grey["100"]
            }
          })
        }
      },
      MuiTooltip: {
        styleOverrides: {
          arrow: ({ theme }) => ({
            color: theme.vars.palette.text.primary
          }),
          popperArrow: ({ theme }) => ({
            color: theme.vars.palette.text.primary
          }),
          tooltip: ({ theme }) => ({
            backgroundColor: theme.vars.palette.text.primary,
            color: theme.vars.palette.background.default
          })
        }
      }
    },
    shadows: createShadows(8, tidalColors, colorMode),
    typography: {
      button: {
        textTransform: "none"
      },
      h7: {
        fontSize: "1.125rem",
        fontWeight: 700,
        letterSpacing: "0.15px",
        lineHeight: 1.2
      },
      subtitle3: {
        fontSize: "1rem",
        fontWeight: 500,
        letterSpacing: "0.15px",
        lineHeight: 1.5
      },
      subtitle4: {
        fontSize: "0.875rem",
        fontWeight: 700,
        letterSpacing: "0.25px",
        lineHeight: 1.43
      }
    }
  };
}
export {
  createTidalThemeOptions
};
//# sourceMappingURL=createTidalThemeOptions.js.map
